/**
 * ReportPreview - Displays a generated report with all sections
 */

import { useTranslation } from 'react-i18next';
import type {
  Report,
  ReportProjectMetrics,
  ReportTeamMetrics,
} from '@services/api';

interface ReportPreviewProps {
  report: Report;
  getHealthScoreColor?: (score: number) => string;
}

export function ReportPreview({
  report,
  getHealthScoreColor = (score) => {
    if (score >= 80) return 'text-green-600 dark:text-green-400';
    if (score >= 60) return 'text-yellow-600 dark:text-yellow-400';
    return 'text-red-600 dark:text-red-400';
  },
}: ReportPreviewProps) {
  const { t } = useTranslation();

  const formatDate = (date: Date) => {
    return new Intl.DateTimeFormat(undefined, {
      dateStyle: 'medium',
      timeStyle: 'short',
    }).format(date);
  };

  const formatShortDate = (date: Date | string | undefined) => {
    if (!date) return '-';
    const d = typeof date === 'string' ? new Date(date) : date;
    return new Intl.DateTimeFormat(undefined, {
      dateStyle: 'short',
    }).format(d);
  };

  const getPriorityColor = (priority: string) => {
    switch (priority.toLowerCase()) {
      case 'critical':
        return 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400';
      case 'high':
        return 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400';
      case 'medium':
        return 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400';
      case 'low':
        return 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400';
      default:
        return 'bg-neutral-100 text-neutral-700 dark:bg-neutral-900/30 dark:text-neutral-400';
    }
  };

  return (
    <div className="bg-white dark:bg-neutral-800 rounded-xl shadow-sm border border-neutral-200 dark:border-neutral-700 overflow-hidden">
      {/* Report Header */}
      <div className="bg-gradient-to-r from-primary-600 to-primary-700 px-6 py-5 text-white">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold">
              {t(`reports.types.${report.reportType}`)}
            </h2>
            <p className="text-sm text-primary-100 mt-1">
              {t('reports.generatedAt', { date: formatDate(report.generatedAt) })}
            </p>
          </div>
          <div className="text-right text-sm text-primary-100">
            <p>{t('reports.period')}</p>
            <p className="font-medium text-white">
              {formatShortDate(report.periodStart)} - {formatShortDate(report.periodEnd)}
            </p>
          </div>
        </div>
      </div>

      <div className="p-6 space-y-8">
        {/* AI Summary Section */}
        {report.aiSummary && (
          <section>
            <div className="flex items-center gap-2 mb-4">
              <svg
                className="w-5 h-5 text-primary-500"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
                />
              </svg>
              <h3 className="text-lg font-semibold text-neutral-900 dark:text-white">
                {t('reports.aiSummary')}
              </h3>
              <span className="px-2 py-0.5 text-xs bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-400 rounded-full">
                AI
              </span>
            </div>

            <div className="bg-primary-50 dark:bg-primary-900/20 rounded-xl p-5 border border-primary-100 dark:border-primary-800">
              {/* Executive Summary */}
              <div className="mb-4">
                <h4 className="text-sm font-medium text-neutral-600 dark:text-neutral-400 mb-2">
                  {t('reports.executiveSummary')}
                </h4>
                <p className="text-neutral-800 dark:text-neutral-200">
                  {report.aiSummary.executiveSummary}
                </p>
              </div>

              {/* Key Insights */}
              {report.aiSummary.keyInsights.length > 0 && (
                <div className="mb-4">
                  <h4 className="text-sm font-medium text-neutral-600 dark:text-neutral-400 mb-2">
                    {t('reports.keyInsights')}
                  </h4>
                  <ul className="space-y-1">
                    {report.aiSummary.keyInsights.map((insight, idx) => (
                      <li
                        key={idx}
                        className="flex items-start gap-2 text-sm text-neutral-700 dark:text-neutral-300"
                      >
                        <svg
                          className="w-4 h-4 text-primary-500 flex-shrink-0 mt-0.5"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M13 10V3L4 14h7v7l9-11h-7z"
                          />
                        </svg>
                        {insight}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Action Items */}
              {report.aiSummary.actionItems.length > 0 && (
                <div className="mb-4">
                  <h4 className="text-sm font-medium text-neutral-600 dark:text-neutral-400 mb-2">
                    {t('reports.actionItems')}
                  </h4>
                  <ul className="space-y-1">
                    {report.aiSummary.actionItems.map((item, idx) => (
                      <li
                        key={idx}
                        className="flex items-start gap-2 text-sm text-neutral-700 dark:text-neutral-300"
                      >
                        <svg
                          className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                          />
                        </svg>
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Risk & Outlook */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {report.aiSummary.riskAssessment && (
                  <div className="bg-white/50 dark:bg-neutral-800/50 rounded-lg p-3">
                    <h4 className="text-sm font-medium text-red-600 dark:text-red-400 mb-1">
                      {t('reports.riskAssessment')}
                    </h4>
                    <p className="text-xs text-neutral-600 dark:text-neutral-400">
                      {report.aiSummary.riskAssessment}
                    </p>
                  </div>
                )}
                {report.aiSummary.outlook && (
                  <div className="bg-white/50 dark:bg-neutral-800/50 rounded-lg p-3">
                    <h4 className="text-sm font-medium text-blue-600 dark:text-blue-400 mb-1">
                      {t('reports.outlook')}
                    </h4>
                    <p className="text-xs text-neutral-600 dark:text-neutral-400">
                      {report.aiSummary.outlook}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </section>
        )}

        {/* Project Metrics */}
        {report.projectMetrics.length > 0 && (
          <section>
            <h3 className="text-lg font-semibold text-neutral-900 dark:text-white mb-4 flex items-center gap-2">
              <svg
                className="w-5 h-5 text-neutral-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
                />
              </svg>
              {t('reports.projectMetrics')}
            </h3>

            <div className="space-y-4">
              {report.projectMetrics.map((project) => (
                <ProjectMetricsCard
                  key={project.projectId}
                  project={project}
                  getHealthScoreColor={getHealthScoreColor}
                />
              ))}
            </div>
          </section>
        )}

        {/* Team Metrics */}
        {report.teamMetrics.length > 0 && (
          <section>
            <h3 className="text-lg font-semibold text-neutral-900 dark:text-white mb-4 flex items-center gap-2">
              <svg
                className="w-5 h-5 text-neutral-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                />
              </svg>
              {t('reports.teamPerformance')}
            </h3>

            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-neutral-200 dark:border-neutral-700">
                    <th className="text-left py-3 px-4 text-sm font-medium text-neutral-500">
                      {t('reports.member')}
                    </th>
                    <th className="text-right py-3 px-4 text-sm font-medium text-neutral-500">
                      {t('reports.assigned')}
                    </th>
                    <th className="text-right py-3 px-4 text-sm font-medium text-neutral-500">
                      {t('reports.completed')}
                    </th>
                    <th className="text-right py-3 px-4 text-sm font-medium text-neutral-500">
                      {t('reports.overdue')}
                    </th>
                    <th className="text-right py-3 px-4 text-sm font-medium text-neutral-500">
                      {t('reports.productivity')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {report.teamMetrics.map((member) => (
                    <TeamMetricsRow key={member.memberId} member={member} />
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        )}

        {/* Overdue Tasks */}
        {report.overdueTasks.length > 0 && (
          <section>
            <h3 className="text-lg font-semibold text-neutral-900 dark:text-white mb-4 flex items-center gap-2">
              <svg
                className="w-5 h-5 text-red-500"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              {t('reports.overdueTasks')}
              <span className="ml-2 px-2 py-0.5 text-xs bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 rounded-full">
                {report.overdueTasks.length}
              </span>
            </h3>

            <div className="space-y-2">
              {report.overdueTasks.slice(0, 10).map((task) => (
                <div
                  key={task.id}
                  className="flex items-center justify-between p-3 bg-red-50 dark:bg-red-900/10 rounded-lg border border-red-100 dark:border-red-900/30"
                >
                  <div className="flex items-center gap-3">
                    <span className="text-xs font-mono text-neutral-500">
                      {task.taskKey}
                    </span>
                    <span className="text-sm text-neutral-800 dark:text-neutral-200">
                      {task.title}
                    </span>
                    <span className={`text-xs px-2 py-0.5 rounded ${getPriorityColor(task.priority)}`}>
                      {task.priority}
                    </span>
                  </div>
                  <span className="text-sm text-red-600 dark:text-red-400 font-medium">
                    {task.daysOverdue} {t('reports.daysOverdue')}
                  </span>
                </div>
              ))}
              {report.overdueTasks.length > 10 && (
                <p className="text-sm text-neutral-500 text-center py-2">
                  {t('reports.andMore', { count: report.overdueTasks.length - 10 })}
                </p>
              )}
            </div>
          </section>
        )}

        {/* Upcoming Deadlines */}
        {report.upcomingDeadlines.length > 0 && (
          <section>
            <h3 className="text-lg font-semibold text-neutral-900 dark:text-white mb-4 flex items-center gap-2">
              <svg
                className="w-5 h-5 text-yellow-500"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              {t('reports.upcomingDeadlines')}
            </h3>

            <div className="space-y-2">
              {report.upcomingDeadlines.slice(0, 10).map((task) => (
                <div
                  key={task.id}
                  className="flex items-center justify-between p-3 bg-yellow-50 dark:bg-yellow-900/10 rounded-lg border border-yellow-100 dark:border-yellow-900/30"
                >
                  <div className="flex items-center gap-3">
                    <span className="text-xs font-mono text-neutral-500">
                      {task.taskKey}
                    </span>
                    <span className="text-sm text-neutral-800 dark:text-neutral-200">
                      {task.title}
                    </span>
                    <span className={`text-xs px-2 py-0.5 rounded ${getPriorityColor(task.priority)}`}>
                      {task.priority}
                    </span>
                  </div>
                  <span className="text-sm text-yellow-600 dark:text-yellow-400 font-medium">
                    {task.daysUntilDue === 0
                      ? t('reports.dueToday')
                      : task.daysUntilDue === 1
                        ? t('reports.dueTomorrow')
                        : t('reports.dueInDays', { days: task.daysUntilDue })}
                  </span>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Recent Completions */}
        {report.recentCompletions.length > 0 && (
          <section>
            <h3 className="text-lg font-semibold text-neutral-900 dark:text-white mb-4 flex items-center gap-2">
              <svg
                className="w-5 h-5 text-green-500"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              {t('reports.recentCompletions')}
            </h3>

            <div className="space-y-2">
              {report.recentCompletions.slice(0, 10).map((task) => (
                <div
                  key={task.id}
                  className="flex items-center justify-between p-3 bg-green-50 dark:bg-green-900/10 rounded-lg border border-green-100 dark:border-green-900/30"
                >
                  <div className="flex items-center gap-3">
                    <span className="text-xs font-mono text-neutral-500">
                      {task.taskKey}
                    </span>
                    <span className="text-sm text-neutral-800 dark:text-neutral-200">
                      {task.title}
                    </span>
                  </div>
                  <span className="text-sm text-green-600 dark:text-green-400">
                    {formatShortDate(task.completedAt)}
                  </span>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Highlights, Concerns & Recommendations */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Highlights */}
          {report.highlights.length > 0 && (
            <div className="bg-green-50 dark:bg-green-900/10 rounded-xl p-5 border border-green-100 dark:border-green-900/30">
              <h4 className="font-medium text-green-800 dark:text-green-300 mb-3 flex items-center gap-2">
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z"
                  />
                </svg>
                {t('reports.highlights')}
              </h4>
              <ul className="space-y-2">
                {report.highlights.map((item, idx) => (
                  <li key={idx} className="text-sm text-green-700 dark:text-green-400 flex items-start gap-2">
                    <span className="text-green-500">+</span>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Concerns */}
          {report.concerns.length > 0 && (
            <div className="bg-red-50 dark:bg-red-900/10 rounded-xl p-5 border border-red-100 dark:border-red-900/30">
              <h4 className="font-medium text-red-800 dark:text-red-300 mb-3 flex items-center gap-2">
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                  />
                </svg>
                {t('reports.concerns')}
              </h4>
              <ul className="space-y-2">
                {report.concerns.map((item, idx) => (
                  <li key={idx} className="text-sm text-red-700 dark:text-red-400 flex items-start gap-2">
                    <span className="text-red-500">!</span>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Recommendations */}
          {report.recommendations.length > 0 && (
            <div className="bg-blue-50 dark:bg-blue-900/10 rounded-xl p-5 border border-blue-100 dark:border-blue-900/30">
              <h4 className="font-medium text-blue-800 dark:text-blue-300 mb-3 flex items-center gap-2">
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
                  />
                </svg>
                {t('reports.recommendations')}
              </h4>
              <ul className="space-y-2">
                {report.recommendations.map((item, idx) => (
                  <li key={idx} className="text-sm text-blue-700 dark:text-blue-400 flex items-start gap-2">
                    <span className="text-blue-500">&rarr;</span>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Sub-components
function ProjectMetricsCard({
  project,
  getHealthScoreColor,
}: {
  project: ReportProjectMetrics;
  getHealthScoreColor: (score: number) => string;
}) {
  const { t } = useTranslation();

  return (
    <div className="bg-neutral-50 dark:bg-neutral-900/50 rounded-xl p-5 border border-neutral-200 dark:border-neutral-700">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h4 className="font-semibold text-neutral-900 dark:text-white">
            {project.projectName}
          </h4>
          <span className="text-xs text-neutral-500">{project.projectKey}</span>
        </div>
        <div className="text-right">
          <span className={`text-2xl font-bold ${getHealthScoreColor(project.healthScore)}`}>
            {Math.round(project.healthScore)}%
          </span>
          <p className="text-xs text-neutral-500">{t('reports.healthScore')}</p>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <MetricItem
          label={t('reports.totalTasks')}
          value={project.taskMetrics.totalTasks}
        />
        <MetricItem
          label={t('reports.completed')}
          value={project.taskMetrics.completedTasks}
          color="text-green-600 dark:text-green-400"
        />
        <MetricItem
          label={t('reports.inProgress')}
          value={project.taskMetrics.inProgressTasks}
          color="text-blue-600 dark:text-blue-400"
        />
        <MetricItem
          label={t('reports.overdue')}
          value={project.taskMetrics.overdueTasks}
          color="text-red-600 dark:text-red-400"
        />
      </div>

      {/* Completion Progress */}
      <div className="mt-4">
        <div className="flex justify-between text-xs text-neutral-500 mb-1">
          <span>{t('reports.completionRate')}</span>
          <span>{Math.round(project.taskMetrics.completionRate)}%</span>
        </div>
        <div className="h-2 bg-neutral-200 dark:bg-neutral-700 rounded-full overflow-hidden">
          <div
            className="h-full bg-primary-500 rounded-full transition-all"
            style={{ width: `${Math.min(100, project.taskMetrics.completionRate)}%` }}
          />
        </div>
      </div>
    </div>
  );
}

function MetricItem({
  label,
  value,
  color,
}: {
  label: string;
  value: number;
  color?: string;
}) {
  return (
    <div>
      <p className="text-xs text-neutral-500">{label}</p>
      <p className={`text-lg font-semibold ${color || 'text-neutral-900 dark:text-white'}`}>
        {value}
      </p>
    </div>
  );
}

function TeamMetricsRow({ member }: { member: ReportTeamMetrics }) {
  const productivityColor =
    member.productivityScore >= 80
      ? 'text-green-600 dark:text-green-400'
      : member.productivityScore >= 60
        ? 'text-yellow-600 dark:text-yellow-400'
        : 'text-red-600 dark:text-red-400';

  return (
    <tr className="border-b border-neutral-100 dark:border-neutral-700/50 hover:bg-neutral-50 dark:hover:bg-neutral-700/50">
      <td className="py-3 px-4">
        <div>
          <p className="text-sm font-medium text-neutral-900 dark:text-white">
            {member.memberName}
          </p>
          <p className="text-xs text-neutral-500">{member.memberEmail}</p>
        </div>
      </td>
      <td className="py-3 px-4 text-right text-sm text-neutral-700 dark:text-neutral-300">
        {member.tasksAssigned}
      </td>
      <td className="py-3 px-4 text-right text-sm text-green-600 dark:text-green-400 font-medium">
        {member.tasksCompleted}
      </td>
      <td className="py-3 px-4 text-right text-sm text-red-600 dark:text-red-400">
        {member.tasksOverdue}
      </td>
      <td className="py-3 px-4 text-right">
        <span className={`text-sm font-semibold ${productivityColor}`}>
          {Math.round(member.productivityScore)}%
        </span>
      </td>
    </tr>
  );
}

export default ReportPreview;
